import clsx from 'clsx';
import { Dispatch, useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogBody,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  Badge,
  Box,
  Button,
  Card,
  CardHeader,
  Field,
  Flex,
  Menu,
  MenuContent,
  MenuTrigger,
  Text,
  Tooltip,
} from '@optiaxiom/react';
import { IconEllipsisSolid, IconPlus } from '@optimizely/axiom-icons';

import type { SpecializedAgentAction } from '@frontend/features/agents/hooks';
import { useUnusedParameters } from '@frontend/features/agents/hooks/useUnusedParameters';
import * as agentStyles from '@frontend/styles/form.css';
import { AgentParameter, AgentParameterType } from '@frontend/types';

import { ParameterForm } from './ParameterForm';
import { styles } from './VariableInput.css';

type VariableInputProps = {
  parameters: Array<AgentParameter<AgentParameterType>>;
  dispatch: Dispatch<SpecializedAgentAction>;
  promptTemplate?: string;
  shouldShowUnusedParamsWarning: boolean;
};

export function VariableRow({
  variable,
  onDelete,
  onEdit,
  isUnused,
  isReadOnly,
}: {
  variable: AgentParameter<AgentParameterType>;
  onDelete: () => void;
  onEdit?: () => void;
  isUnused?: boolean;
  isReadOnly?: boolean;
}) {
  return (
    <Card className={clsx({ [styles.unusedVariable]: isUnused })}>
      <CardHeader
        description={
          <Tooltip content={variable.description || ''}>
            <Box maxW="xs">
              <Text truncate>{variable.description || ''}</Text>
            </Box>
          </Tooltip>
        }
        addonAfter={
          <Flex flexDirection="row" gap="8" alignItems="center">
            {isUnused && <Badge intent="warning">Not in use</Badge>}
            <Badge intent="neutral">{variable.type}</Badge>
            <Badge intent="information">{variable.required ? 'Required' : 'Optional'}</Badge>
            {!isReadOnly && (
              <Menu
                options={[
                  {
                    label: 'Edit',
                    execute: onEdit,
                  },
                  {
                    label: 'Delete',
                    execute: onDelete,
                  },
                ]}
              >
                <MenuTrigger
                  appearance="subtle"
                  aria-label="Variable actions"
                  icon={<IconEllipsisSolid size={16} />}
                />
                <MenuContent />
              </Menu>
            )}
          </Flex>
        }
      >
        <Box maxW="xs">
          <Text truncate>{variable.name}</Text>
        </Box>
      </CardHeader>
    </Card>
  );
}

export function VariableInput({
  parameters,
  dispatch,
  promptTemplate,
  shouldShowUnusedParamsWarning,
}: VariableInputProps) {
  const parameterText =
    'Specify variables for your agent. Once added, refer to them in your prompt template with [[variable_name]]';

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [parameterToDelete, setParameterToDelete] = useState<number | null>(null);

  const [parameterFormOpen, setParameterFormOpen] = useState(false);
  const [editingParameter, setEditingParameter] = useState<
    AgentParameter<AgentParameterType> | undefined
  >(undefined);
  const [newlyCreatedParams, setNewlyCreatedParams] = useState<Set<string>>(new Set());

  const { unusedParams } = useUnusedParameters(parameters, promptTemplate);
  const unusedParamNames = new Set(unusedParams.filter(p => p.type !== 'file').map(p => p.name));

  const handleAddParameter = () => {
    setEditingParameter(undefined);
    setParameterFormOpen(true);
  };

  const handleEditParameter = (parameter: AgentParameter<AgentParameterType>) => {
    setEditingParameter(parameter);
    setParameterFormOpen(true);
  };

  const handleSaveParameter = (values: AgentParameter<AgentParameterType>) => {
    const typeMap: Record<string, AgentParameter<AgentParameterType>['type']> = {
      string: 'string',
      number: 'number',
      boolean: 'boolean',
      array: 'object',
      object: 'object',
      file: 'file',
      datetime: 'datetime',
    };
    const agentParameter = {
      ...values,
      name: values.name.trim(),
      type: typeMap[values.type] || 'string',
    };
    let updatedParameters;
    if (editingParameter) {
      updatedParameters = parameters.map(v =>
        v.name === editingParameter.name ? { ...v, ...agentParameter } : v
      );
    } else {
      updatedParameters = [...parameters, agentParameter];
      setNewlyCreatedParams(prev => new Set(prev).add(agentParameter.name));
    }
    dispatch({ type: 'UPDATE_NESTED', field: 'parameters', value: updatedParameters });
    setParameterFormOpen(false);
    setEditingParameter(undefined);
  };

  const handleCancelParameter = () => {
    setParameterFormOpen(false);
    setEditingParameter(undefined);
  };

  return (
    <Flex flexDirection="column" gap="8" className={agentStyles.FormInputFieldMaxWidth}>
      <Flex flexDirection="row" gap="16" justifyContent="space-between">
        <Field
          label="Variables"
          info="Placeholders for dynamic input that is provided each time the agent runs. This allows you to reuse the same agent with different inputs."
        />
        {parameters && parameters.length > 0 && (
          <Button
            appearance="default"
            addonBefore={<IconPlus size="16" />}
            onClick={handleAddParameter}
          >
            Add Variable
          </Button>
        )}
      </Flex>
      {parameters.length > 0 ? (
        <Flex flexDirection="column" gap="8">
          {parameters.map((parameter, index) => (
            <VariableRow
              key={parameter.name || `param-${index}`}
              variable={parameter}
              onDelete={() => {
                setParameterToDelete(index);
                setDeleteDialogOpen(true);
              }}
              onEdit={() => handleEditParameter(parameter)}
              isUnused={
                unusedParamNames.has(parameter.name) &&
                (shouldShowUnusedParamsWarning || !newlyCreatedParams.has(parameter.name))
              }
            />
          ))}
        </Flex>
      ) : (
        <Flex
          className={styles.emptySection}
          justifyContent="center"
          alignItems="center"
          flexDirection="column"
          bg="bg.secondary"
          p="20"
        >
          <Text color="fg.secondary">{parameterText}</Text>
          <Button
            appearance="subtle"
            addonBefore={<IconPlus size="16" />}
            onClick={handleAddParameter}
          >
            Add Variable
          </Button>
        </Flex>
      )}

      {/* ParameterForm Dialog for add/edit */}
      {parameterFormOpen && (
        <ParameterForm
          open={parameterFormOpen}
          onOpenChange={setParameterFormOpen}
          initialValues={editingParameter}
          onSave={handleSaveParameter}
          onCancel={handleCancelParameter}
        />
      )}

      {/* AlertDialog for delete confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>Delete Variable?</AlertDialogHeader>
          <AlertDialogBody>
            Are you sure you want to delete the variable{' '}
            {parameterToDelete !== null && parameters[parameterToDelete]?.name
              ? parameters[parameterToDelete].name
              : ''}
            ? This action cannot be undone.
          </AlertDialogBody>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteDialogOpen(false)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              appearance="danger"
              onClick={() => {
                if (parameterToDelete !== null) {
                  const updatedParameters = parameters.filter((_, i) => i !== parameterToDelete);
                  dispatch({
                    type: 'UPDATE_NESTED',
                    field: 'parameters',
                    value: updatedParameters,
                  });
                }
                setDeleteDialogOpen(false);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Flex>
  );
}
